import ObexRequestHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { Book } from '../types/book.types';
import { buildQuery, unwrapList, getErrorMessage, getAccessToken } from './apiUtils';
import { getWarehouseId } from './TerminalService';

/**
 * /books_list no tiene carpeta, asi que ObexRequestHandlerP80.toGatewayPath la
 * deja igual: la misma ruta vale para el servidor local y para el gateway.
 */
const ROUTES = {
    list: '/books_list'
};

const DEFAULT_PAGE_SIZE = 25;

/** Cuantos libros se traen para sortear uno en el escaneo simulado. */
const RANDOM_POOL_SIZE = 50;

export const NO_SESSION = 'Sesion caducada. Vuelve a identificarte.';

/**
 * La tabla books no guarda peso, y el stock lo agrega books_list (SUM sobre
 * stock_movements, verificado en local: Don Quijote sale con 100).
 *
 * - weight: 0. useCartTotals aplica `item.weight || 0.5`, asi que el peso
 *   estimado por defecto sigue funcionando.
 * - stock: existencias reales de TODOS los almacenes; books_list no filtra por
 *   el almacen del terminal. Un libro archivado se fuerza a 0 aunque le queden
 *   unidades. Si el backend no manda el campo se da por disponible: poner 0
 *   dejaria el catalogo entero sin vender, que es peor que vender de mas.
 * - sources: no viene del backend, queda sin definir.
 */
const UNKNOWN_STOCK = 1;

const mapStock = (raw: any): number => {
    if (raw.status === 'archived') return 0;
    if (raw.stock === undefined || raw.stock === null) return UNKNOWN_STOCK;
    const stock = Number(raw.stock);
    return isNaN(stock) ? UNKNOWN_STOCK : stock;
};

export const mapBook = (raw: any): Book => ({
    id:       String(raw.id),
    title:    raw.title || '',
    author:   raw.author || '',
    price:    isNaN(Number(raw.price)) ? 0 : Number(raw.price),
    weight:   0,
    isbn:     raw.isbn || '',
    publisher: raw.publisher || '',
    category: raw.category || '',
    stock:    mapStock(raw)
});

/**
 * Busca en el catalogo. El backend hace ILIKE sobre title, isbn y author.
 *
 * warehouse_id solo va si el terminal tiene almacen asignado (lo guarda
 * AuthService en el login): buildQuery omite los parametros vacios, asi que sin
 * almacen la llamada sale igual que antes, con el stock de todos los almacenes.
 *
 * OJO: books/list.avap no usa warehouse_id solo para el SUM del stock, tambien
 * mete un EXISTS sobre stock_movements, asi que un libro que nunca haya pasado
 * por ese almacen NO SALE en los resultados (no sale "sin stock": no sale).
 * Verificado en local: sin almacen 15 libros, con el 4 salen 8, con el 2 ninguno.
 */
export const searchBooks = async (
    search: string,
    pageSize = DEFAULT_PAGE_SIZE,
    /**
     * Almacen por el que filtrar. Por defecto el del terminal, que es lo que
     * quiere la caja. Con '' se busca en TODO el catalogo, sin el EXISTS que
     * se explica arriba: lo necesita la factura, que busca el libro para sacar
     * su ISBN y ahi el stock da igual.
     */
    warehouseId = getWarehouseId()
): Promise<Book[]> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error(NO_SESSION);

    const res = await ObexRequestHandlerP80.get(
        `${ROUTES.list}${buildQuery({
            access_token: accessToken,
            search:       search,
            warehouse_id: warehouseId,
            page:         1,
            page_size:    pageSize
        })}`
    );

    const error = getErrorMessage(res);
    if (error) throw new Error(error);

    return unwrapList(res).map(mapBook);
};

/**
 * Existencias de un libro en OTRO almacen.
 *
 * Reutiliza books_list con un warehouse_id distinto al del terminal. Hay que
 * tener en cuenta el EXISTS que comenta searchBooks: si el libro nunca ha
 * pasado por ese almacen NO SALE en la respuesta, y eso aqui se lee como 0.
 *
 * Una llamada por almacen. Mientras sean unas pocas librerias sale a cuenta;
 * si crecen, lo suyo es pedirle a backend un endpoint que devuelva el stock de
 * un libro desglosado por almacen y cambiar solo esta funcion.
 */
export const stockEnAlmacen = async (isbnOrId: string, warehouseId: string): Promise<number> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error(NO_SESSION);

    const res = await ObexRequestHandlerP80.get(
        `${ROUTES.list}${buildQuery({
            access_token: accessToken,
            search:       isbnOrId,
            warehouse_id: warehouseId,
            page:         1,
            page_size:    5
        })}`
    );

    const error = getErrorMessage(res);
    if (error) throw new Error(error);

    const limpio = (v: string) => String(v || '').replace(/[^0-9a-zA-Z]/g, '').toUpperCase();
    const objetivo = limpio(isbnOrId);
    const fila = unwrapList(res)
        .map(mapBook)
        .filter(b => limpio(b.isbn) === objetivo || String(b.id) === String(isbnOrId))[0];

    return fila ? Math.max(0, fila.stock) : 0;
};

/**
 * Un libro cualquiera del catalogo real, para el escaneo simulado. books_list
 * admite la peticion sin `search`, asi que devuelve el catalogo entero y de ahi
 * se sortea uno.
 */
export const getRandomBook = async (): Promise<Book | undefined> => {
    const books = await searchBooks('', RANDOM_POOL_SIZE);
    if (books.length === 0) return undefined;
    return books[Math.floor(Math.random() * books.length)];
};

/**
 * El numpad solo teclea digitos, pero los ISBN se suelen guardar con guiones
 * (978-84-1234-567-8). Comparamos sin separadores para que 9788412345678
 * encuentre igual.
 */
const normalizeRef = (value: string) => value.replace(/[\s-]/g, '').toUpperCase();

/**
 * Busca por referencia exacta (ISBN o id), para el escaner y la entrada manual
 * del numpad. books_list hace ILIKE, asi que puede devolver parciales: filtramos
 * a coincidencia exacta igual que hacia findProductByReference con MOCK_BOOKS.
 */
export const findBookByReference = async (ref: string): Promise<Book | undefined> => {
    const cleanRef = ref.trim();
    if (!cleanRef) return undefined;

    const target = normalizeRef(cleanRef);
    const results = await searchBooks(cleanRef);

    return results.find(b =>
        normalizeRef(b.isbn) === target ||
        normalizeRef(b.id) === target
    );
};

/** Cuantos libros del catalogo se miran para encontrar los de la misma categoria. */
const CATEGORY_POOL_SIZE = 100;

export const MAX_RECOMMENDATIONS = 3;

export interface BookRecommendation {
    book: Book;
    reason: string;
}

const sameText = (a: string, b: string): boolean =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Sugerencias de venta para una linea del ticket: libros del MISMO AUTOR y, si
 * faltan, de la MISMA CATEGORIA, hasta MAX_RECOMMENDATIONS. Es la regla del TPV
 * de referencia (tpv-albatross-4), llevada al catalogo real.
 *
 * - books_list busca por texto en titulo, ISBN y autor, no por categoria: los
 *   del mismo autor salen de buscar su nombre y se confirman con igualdad exacta
 *   (el ILIKE devuelve tambien parciales); los de la misma categoria se sacan
 *   de las primeras CATEGORY_POOL_SIZE entradas del catalogo, filtradas aqui.
 * - Se descartan el propio libro, los que ya estan en el ticket y los sin
 *   stock: no se sugiere lo que no se puede vender.
 */
export const getRecommendations = async (
    item: { id: string; author: string; category: string },
    excludeIds: string[] = []
): Promise<BookRecommendation[]> => {
    const skip = new Set<string>([item.id, ...excludeIds]);
    const sellable = (b: Book) => !skip.has(b.id) && b.stock > 0;

    const [byAuthorRaw, poolRaw] = await Promise.all([
        item.author ? searchBooks(item.author) : Promise.resolve([] as Book[]),
        item.category ? searchBooks('', CATEGORY_POOL_SIZE) : Promise.resolve([] as Book[])
    ]);

    const picked: BookRecommendation[] = [];
    const taken = new Set<string>();
    const add = (b: Book, reason: string) => {
        if (picked.length >= MAX_RECOMMENDATIONS || taken.has(b.id)) return;
        taken.add(b.id);
        picked.push({ book: b, reason });
    };

    byAuthorRaw
        .filter(b => sellable(b) && sameText(b.author, item.author))
        .forEach(b => add(b, `Otro éxito de ${b.author}`));

    poolRaw
        .filter(b => sellable(b) && sameText(b.category, item.category))
        .forEach(b => add(b, `Si te gusta la ${b.category.toLowerCase()}, te encantará este libro.`));

    return picked;
};

